// The document's filing drawer, with a way to add one without leaving the form.
//
// Categories are a managed lookup like payment methods, expense categories and
// outlets — but a partner uploading a signed agreement into a drawer that does not
// exist yet should not have to abandon the upload, walk to Settings, and come back.
// So creating one goes through the SAME settings endpoint the Settings screen uses:
// the code is generated server-side from the Hebrew label, the list stays canonical,
// and a reload() makes the new value visible everywhere else in the app at once.
//
// The action sits BESIDE the select, bordered, on one line (.doc-category) — the
// same shape the outlet picker uses. Below it, which is where .field's column
// layout puts any extra child, a borderless button stretches to full width and
// reads as a stray line of text rather than something to press.
import { useState } from 'react';
import { useLookups } from '../../context/LookupsContext';
import { settingsApi } from '../../api';
import he from '../../i18n/he';
import Button from '../../components/Button';
import { Field } from '../../components/Form';
import { ErrorText } from '../../components/Loader';

export default function CategorySelect({ value, onChange, options }) {
  const lookups = useLookups();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const create = async () => {
    const name = label.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const created = await settingsApi.add('document-categories', { label_he: name });
      await lookups.reload();
      onChange(created.code); // select what was just created — that was the intent
      setLabel('');
      setAdding(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => { setAdding(false); setLabel(''); setError(null); };

  return (
    <Field label={he.documents.category}>
      {adding ? (
        <div className="doc-category-new">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={he.documents.categoryAddPrompt}
            // Enter adds the category rather than submitting the whole form, which
            // would otherwise upload with the category still unset.
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); create(); }
              if (e.key === 'Escape') cancel();
            }}
            autoFocus
          />
          <Button small onClick={create} disabled={busy || !label.trim()}>{he.common.add}</Button>
          <Button small variant="subtle" onClick={cancel} disabled={busy}>{he.common.cancel}</Button>
        </div>
      ) : (
        <div className="doc-category">
          <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
            <option value="" disabled>{he.documents.category}</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <Button small variant="subtle" onClick={() => setAdding(true)}>
            {he.documents.categoryAdd}
          </Button>
        </div>
      )}
      {error && <ErrorText error={error} />}
    </Field>
  );
}
